// Ação "Requisição HTTP" das Automações visuais: valores padrão, variáveis,
// inserção na posição do cursor e caminhos JSON das amostras de teste.

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
export const HTTP_BODY_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
export const HTTP_MIN_TIMEOUT = 1;
export const HTTP_MAX_TIMEOUT = 15;
export const HTTP_SAMPLE_MAX_CHARS = 16000;
const MAX_JSON_ENTRIES = 200;

export const HTTP_BASE_VARIABLES = [
  { token: 'contact.name', label: 'Nome do contato', group: 'Contato' },
  { token: 'contact.phone', label: 'Telefone do contato', group: 'Contato' },
  { token: 'contact.email', label: 'E-mail do contato', group: 'Contato' },
  { token: 'contact.cpf', label: 'CPF do contato', group: 'Contato' },
  { token: 'conversation.id', label: 'Número da conversa', group: 'Conversa' },
  {
    token: 'conversation.labels',
    label: 'Etiquetas da conversa',
    group: 'Conversa',
  },
];

export const defaultHttpAction = () => ({
  httpUrl: '',
  httpMethod: 'POST',
  httpHeaders: [],
  httpBody:
    '{\n  "nome": "{{contact.name}}",\n  "telefone": "{{contact.phone}}"\n}',
  httpTimeout: 10,
  saveAs: 'http',
  lastTestSample: null,
});

export const sanitizeSaveAs = value =>
  String(value || '')
    .trim()
    .replace(/[^\w-]/g, '_')
    .slice(0, 40) || 'http';

export const clampHttpTimeout = value => {
  const seconds = Math.round(Number(value));
  if (!Number.isFinite(seconds) || seconds <= 0) return 10;
  return Math.min(HTTP_MAX_TIMEOUT, Math.max(HTTP_MIN_TIMEOUT, seconds));
};

export const variableToken = path => `{{${path}}}`;

// Insere o texto na seleção atual (ou no fim, sem seleção conhecida).
export const insertAtCursor = (value, text, start, end) => {
  const current = String(value || '');
  const from = Number.isInteger(start) ? start : current.length;
  const to = Number.isInteger(end) ? end : from;
  return {
    value: `${current.slice(0, from)}${text}${current.slice(to)}`,
    cursor: from + text.length,
  };
};

const previewValue = value => {
  if (value === null) return 'null';
  if (typeof value === 'string') return value.slice(0, 80);
  return String(value);
};

// Lista plana de caminhos (formato cliente.telefone / itens[0].id) com prévia.
export const flattenJsonEntries = (value, prefix = '', entries = []) => {
  if (entries.length >= MAX_JSON_ENTRIES) return entries;
  if (Array.isArray(value)) {
    value.slice(0, 20).forEach((child, index) => {
      flattenJsonEntries(
        child,
        prefix ? `${prefix}[${index}]` : `[${index}]`,
        entries
      );
    });
    if (!value.length && prefix)
      entries.push({ path: prefix, preview: '[]', depth: 0 });
    return entries;
  }
  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, child]) => {
      flattenJsonEntries(child, prefix ? `${prefix}.${key}` : key, entries);
    });
    return entries;
  }
  if (prefix) {
    entries.push({
      path: prefix,
      preview: previewValue(value),
      depth: prefix.split(/[.[]/).length - 1,
    });
  }
  return entries.slice(0, MAX_JSON_ENTRIES);
};

export const truncateHttpSample = body => {
  try {
    const serialized = JSON.stringify(body);
    if (serialized === undefined) return null;
    if (serialized.length <= HTTP_SAMPLE_MAX_CHARS) return body;
    return typeof body === 'string'
      ? body.slice(0, HTTP_SAMPLE_MAX_CHARS)
      : null;
  } catch {
    return null;
  }
};

export const ancestorNodes = (nodes = [], edges = [], nodeId = '') => {
  const found = [];
  const visited = new Set([nodeId]);
  const queue = [nodeId];
  while (queue.length) {
    const current = queue.shift();
    edges
      .filter(edge => edge.target === current && !visited.has(edge.source))
      .forEach(edge => {
        visited.add(edge.source);
        const node = nodes.find(item => item.id === edge.source);
        if (node) {
          found.push(node);
          queue.push(node.id);
        }
      });
  }
  return found;
};

export const isHttpNode = node => node?.action?.type === 'http_request';

export const upstreamHttpNodes = (nodes, edges, nodeId) =>
  ancestorNodes(nodes, edges, nodeId).filter(isHttpNode);

// Variáveis {{http.<saveAs>...}} disponíveis a partir dos nós HTTP anteriores.
export const httpVariablesFromNodes = httpNodes =>
  httpNodes.flatMap(node => {
    const saveAs = sanitizeSaveAs(node.action.saveAs);
    const group = `HTTP · ${node.title || saveAs}`;
    const body = node.action.lastTestSample?.body;
    const bodyPaths =
      body && typeof body === 'object'
        ? flattenJsonEntries(body).map(entry => ({
            token: `http.${saveAs}.${entry.path}`,
            label: entry.path,
            group,
          }))
        : [];
    return [
      { token: `http.${saveAs}._status`, label: 'Status HTTP', group },
      { token: `http.${saveAs}._ok`, label: 'Sucesso (2xx)', group },
      ...bodyPaths,
    ];
  });

export const httpContextSamples = httpNodes =>
  Object.fromEntries(
    httpNodes
      .filter(node => node.action.lastTestSample)
      .map(node => [
        sanitizeSaveAs(node.action.saveAs),
        {
          status: node.action.lastTestSample.status,
          ok: node.action.lastTestSample.ok,
          body: node.action.lastTestSample.body,
        },
      ])
  );

export const validateHttpAction = action => {
  const url = String(action?.httpUrl || '').trim();
  if (!/^https?:\/\/\S+/i.test(url)) {
    return { isValid: false, message: 'URL http ou https' };
  }
  if (!HTTP_METHODS.includes(action?.httpMethod)) {
    return { isValid: false, message: 'Método HTTP' };
  }
  const invalidHeader = (action?.httpHeaders || []).some(
    header =>
      String(header?.key || '').trim() &&
      !/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/.test(String(header.key).trim())
  );
  if (invalidHeader) return { isValid: false, message: 'Nome de cabeçalho' };
  return { isValid: true, message: '' };
};
